import React, { useState, KeyboardEvent, useRef, useEffect } from 'react'

import { css } from 'src/ui/style/css'
import { useTheme } from 'src/ui/style/useTheme'
import { View } from 'src/ui/basic/View'
import { msg } from 'src/i18n/texts/keys'
import { useTran } from 'src/i18n/useTran'
import { isMobileScreen, ScreenSize } from '../../style/ScreenSize'
import { GoPaperAirplane } from 'react-icons/go'

const { Sizes } = ScreenSize

interface ChatInputProps {
    onSendMessage: (message: string) => void
    disabled?: boolean
}

const ChatInput: React.FC<ChatInputProps> = ({
    onSendMessage,
    disabled = false
}) => {
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
    const setTextAreaRef = (el: HTMLTextAreaElement | null) => {
        inputRef.current = el
    }
    const setInputRef = (el: HTMLInputElement | null) => {
        inputRef.current = el
    }
    const t: ReturnType<typeof useTran> = useTran()
    const th: ReturnType<typeof useTheme> = useTheme()
    const isMobile = isMobileScreen()
    const [message, setMessage] = useState<string>('')

    const handleSend: () => void = () => {
        if (message.trim() && !disabled) {
            onSendMessage(message.trim())
            setMessage('')
            // Keep focus on the input so the keyboard stays open on mobile
            inputRef?.current?.focus()
        }
    }

    const handleKeyDown: (
        e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => void = e => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSend()
        }
    }

    const handleChange: (
        e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => void = e => {
        setMessage(e.target.value)
    }

    // Position input above virtual keyboard on mobile.
    // Uses position:fixed + top pinned to the bottom edge of the visual viewport.
    // This works on both Android and iOS regardless of how the layout viewport behaves.
    useEffect(() => {
        if (!isMobile) return

        const el = document.getElementById('chat-input-bar')
        if (!el) return

        const vv = window.visualViewport
        if (!vv) return

        const messagesEl = document.getElementById('chat-messages')

        let scrollLocked = false

        // Block touch scrolling when content fits the visible area
        const onTouchMove = (e: TouchEvent) => {
            if (scrollLocked) {
                e.preventDefault()
            }
        }

        messagesEl?.addEventListener('touchmove', onTouchMove, { passive: false })

        const sync = () => {
            const visibleBottom = vv.offsetTop + vv.height
            const inputH = el.offsetHeight
            const keyboardLift = Math.max(0, window.innerHeight - visibleBottom)
            const kbOpen = keyboardLift > 120

            if (kbOpen) {
                el.style.top = `${window.innerHeight - inputH - keyboardLift}px`
                el.style.bottom = 'auto'
                el.style.paddingBottom = '0.5rem'
            } else {
                el.style.top = ''
                el.style.bottom = ''
                el.style.paddingBottom = ''
            }

            if (messagesEl) {
                const extraSpace = kbOpen ? keyboardLift : 0
                messagesEl.style.marginBottom = `${Math.ceil(
                    inputH + extraSpace
                )}px`

                if (kbOpen) {
                    const visibleH = vv.height - inputH
                    const actualContent = Array.from(messagesEl.children).reduce(
                        (sum, child) => sum + (child as HTMLElement).offsetHeight, 0
                    )
                    const cStyles = getComputedStyle(messagesEl)
                    const paddingTop = parseFloat(cStyles.paddingTop) || 0
                    const paddingBottom = parseFloat(cStyles.paddingBottom) || 0
                    const gap = parseFloat(cStyles.gap) || 0
                    const totalContent = actualContent + paddingTop + paddingBottom
                        + gap * Math.max(0, messagesEl.children.length - 1)

                    scrollLocked = totalContent <= visibleH
                    if (scrollLocked) {
                        messagesEl.scrollTop = 0
                    }
                } else {
                    scrollLocked = false
                }
            }
        }

        // Re-evaluate scroll lock when messages change
        const observer = messagesEl
            ? new MutationObserver(sync)
            : null
        observer?.observe(messagesEl!, { childList: true, subtree: true })

        vv.addEventListener('resize', sync)
        vv.addEventListener('scroll', sync)
        sync()

        return () => {
            vv.removeEventListener('resize', sync)
            vv.removeEventListener('scroll', sync)
            observer?.disconnect()
            messagesEl?.removeEventListener('touchmove', onTouchMove)
            el.style.top = ''
            el.style.bottom = ''
            el.style.paddingBottom = ''
            if (messagesEl) {
                messagesEl.style.marginBottom = ''
            }
        }
    }, [isMobile])

    return (
        <View
            id="chat-input-bar"
            horizontal
            css={css`
                padding: 0.625rem;
                background-color: ${th.colors.white};
                align-items: center;
                flex-shrink: 0;

                @media (max-width: ${Sizes.md}px) {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    padding: 0.5rem;
                    padding-bottom: max(0.5rem, var(--safe-area-bottom));
                    z-index: 1100;
                    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
                }

                @media (orientation: landscape) and (max-height: ${Sizes.landscapeH}px) {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    padding: 0.5rem;
                    padding-bottom: max(0.5rem, var(--safe-area-bottom));
                    z-index: 1100;
                    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
                }
            `}
        >
            <View
                horizontal
                css={css`
                    flex: 1;
                    align-items: center;
                    padding: 0 0.375rem 0 0.875rem;
                    border-radius: 999px;
                    background-color: ${th.colors.white};
                    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
                    border: 1px solid rgba(148, 163, 253, 0.18);
                    box-sizing: border-box;
                    gap: 0.375rem;
                `}
            >
                {isMobile ? (
                    <input
                        ref={setInputRef}
                        value={message}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={t(msg.chatbot.chat.placeholder)}
                        autoFocus
                        css={css`
                            flex: 1;
                            padding: 0.625rem;
                            border: none;
                            font-size: 0.875rem;
                            outline: none;
                            background-color: ${th.colors.white};

                            &:focus {
                                border-color: ${th.colors.primary};
                            }

                            @media (max-width: ${Sizes.md}px) {
                                font-size: 1rem;
                                padding: 0.75rem 1rem;
                                min-height: 2.75rem;
                                box-sizing: border-box;
                            }
                        `}
                    />
                ) : (
                    <textarea
                        ref={setTextAreaRef}
                        value={message}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={t(msg.chatbot.chat.placeholder)}
                        rows={1}
                        autoFocus
                        css={css`
                        flex: 1;
                        padding: 0.625rem 0;
                        border: none;
                        outline: none;
                        resize: none;
                        background: transparent;
                        font-size: 0.875rem;
                        line-height: 1.4;
                        max-height: 4.5rem;
                        overflow-y: auto;
                        &::placeholder {
                            color: ${th.colors.gray};
                        }
                    `}
                    />
                )}
                <button
                    type="button"
                    onClick={handleSend}
                    onMouseDown={e => e.preventDefault()}
                    disabled={!message.trim() || disabled}
                    css={css`
                        margin-left: 0.625rem;
                        width: 2rem;
                        height: 2rem;
                        border-radius: 50%;
                        border: none;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background-color: ${message.trim() && !disabled
                            ? th.colors.primary
                            : th.colors.gray};
                        color: ${th.colors.white};
                        cursor: ${message.trim() && !disabled
                            ? 'pointer'
                            : 'default'};
                        transition: all 0.2s ease;
                        flex-shrink: 0;

                        &:hover {
                            background-color: ${message.trim() && !disabled
                                ? th.colors.secondary
                                : th.colors.gray};
                        }

                        &:active {
                            transform: scale(0.96);
                        }

                        &:disabled {
                            opacity: 0.6;
                        }

                        @media (max-width: ${Sizes.md}px) {
                            width: 2.125rem;
                            height: 2.125rem;
                        }
                    `}
                >
                    <GoPaperAirplane
                        size={20}
                        style={{
                            marginLeft: '0.125rem'
                        }}
                    />
                </button>
            </View>
        </View>
    )
}

export default ChatInput
